import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, UserPlus, Trash2, MessageSquare, Send, ShieldCheck, X, Star, Mail, Megaphone, Pin, PinOff, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { createPortal } from "react-dom";

const TEAL = "#0D9488";

/** Admin card — full CRUD over the coach roster:
 *   • search + add coach by email
 *   • deny / remove (with optional courtesy-email reason)
 *   • one-off email message with history log
 *   • broadcast announcement to all coaches
 *   • pin/unpin a coach as "Featured" (bubbles them to the top of the public directory)
 *   • per-coach client counts + avg rating
 */
export default function AdminCoachesCard() {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgTarget, setMsgTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [featurePending, setFeaturePending] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/coaches")
      .then(({ data }) => setCoaches(Array.isArray(data) ? data : []))
      .catch(() => setCoaches([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = coaches.filter((c) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      (c.email || "").toLowerCase().includes(t) ||
      (c.name || "").toLowerCase().includes(t) ||
      (c.handle || "").toLowerCase().includes(t)
    );
  });

  const addCoach = async (e) => {
    e?.preventDefault?.();
    if (!addEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coaches/add", { email: addEmail.trim() });
      toast.success(data.already_coach ? "User was already a coach" : "Coach added");
      setAddEmail("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggleFeatured = async (c) => {
    const next = !c.is_featured;
    setFeaturePending(c.id);
    // Optimistic update + re-sort so pinned bubbles to top
    setCoaches((prev) => {
      const updated = prev.map((x) => x.id === c.id ? { ...x, is_featured: next } : x);
      updated.sort((a, b) => Number(!!b.is_featured) - Number(!!a.is_featured));
      return updated;
    });
    try {
      await api.patch(`/admin/coaches/${c.id}/feature`, { is_featured: next });
      toast.success(next ? `Pinned ${c.name || c.email} to the directory top` : `Unpinned ${c.name || c.email}`);
    } catch (err) {
      // Rollback
      setCoaches((prev) => {
        const rolled = prev.map((x) => x.id === c.id ? { ...x, is_featured: !next } : x);
        rolled.sort((a, b) => Number(!!b.is_featured) - Number(!!a.is_featured));
        return rolled;
      });
      toast.error(formatApiError(err));
    } finally {
      setFeaturePending(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="admin-coaches-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Coach roster</div>
        <span className="text-[10px] text-slate-400 tabular-nums ml-1">{coaches.length}</span>
      </div>
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <h3 className="font-display font-bold text-xl text-slate-900">All coaches</h3>
        <Button type="button" size="sm" variant="outline"
                onClick={() => setBroadcastOpen(true)}
                disabled={coaches.length === 0}
                className="border-teal-200 text-teal-700 hover:bg-teal-50"
                data-testid="admin-coach-broadcast-open">
          <Megaphone className="w-3.5 h-3.5 mr-1.5" /> Broadcast to all
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Add, message, pin, or remove any coach. Pinned coaches lead the public directory; removal cascades to revoke every active client share.</p>

      {/* Add coach */}
      <form onSubmit={addCoach} className="flex items-end gap-2 mb-4" data-testid="admin-coaches-add-form">
        <div className="flex-1">
          <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Promote existing user by email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={addEmail} onChange={(e) => setAddEmail(e.target.value)}
                   placeholder="user@example.com" type="email"
                   className="pl-9 bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                   data-testid="admin-coach-add-input" />
          </div>
        </div>
        <Button type="submit" disabled={busy || !addEmail.trim()}
                className="text-white h-10" style={{ background: TEAL }}
                data-testid="admin-coach-add-submit">
          <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add coach
        </Button>
      </form>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Filter coaches…"
               className="pl-9 bg-slate-50 border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
               data-testid="admin-coaches-search" />
      </div>

      {loading ? (
        <div className="h-40 bg-slate-50 rounded animate-pulse" />
      ) : filtered.length === 0 ? (
        <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-4 text-center" data-testid="admin-coaches-empty">
          {coaches.length === 0 ? "No coaches yet. Add one above." : "No coaches match this filter."}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="admin-coaches-list">
          {filtered.map((c) => (
            <li key={c.id}
                className={`border rounded-lg p-3 flex items-center gap-3 ${c.is_featured ? "border-amber-300 bg-amber-50/30" : "border-slate-200"}`}
                data-testid={`admin-coach-row-${c.id}`}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-xs"
                   style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}>
                {(c.handle || c.name || c.email || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-900 truncate">{c.name || `@${c.handle || (c.email || "").split("@")[0]}`}</div>
                  {c.is_featured && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-amber-100 text-amber-700"
                          data-testid={`admin-coach-featured-badge-${c.id}`}>
                      <Pin className="w-2.5 h-2.5" /> Featured
                    </span>
                  )}
                  {c.directory_enabled && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-teal-50 text-teal-700">Public</span>
                  )}
                  {!c.accepting_clients && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-slate-100 text-slate-500">Not accepting</span>
                  )}
                  {c.avg_rating !== null && c.rating_count > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-amber-600" data-testid={`admin-coach-rating-${c.id}`}>
                      <Star className="w-3 h-3 fill-current" /> {c.avg_rating.toFixed(1)}
                      <span className="text-slate-400 font-normal ml-0.5">({c.rating_count})</span>
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 font-mono-num truncate">{c.email}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3" data-testid={`admin-coach-counts-${c.id}`}>
                  <span><b className="text-slate-800 tabular-nums">{c.active_client_count}</b> active clients</span>
                  <span className="text-slate-400">·</span>
                  <span><b className="text-slate-800 tabular-nums">{c.lifetime_client_count}</b> lifetime</span>
                </div>
              </div>
              <Button type="button" size="sm" variant="ghost"
                      onClick={() => toggleFeatured(c)}
                      disabled={featurePending === c.id}
                      className={c.is_featured ? "text-amber-700 hover:bg-amber-50 shrink-0" : "text-slate-500 hover:bg-slate-100 shrink-0"}
                      data-testid={`admin-coach-feature-${c.id}`}
                      title={c.is_featured ? "Unpin from directory top" : "Pin to directory top"}>
                {c.is_featured ? <PinOff className="w-3.5 h-3.5 mr-1" /> : <Pin className="w-3.5 h-3.5 mr-1" />}
                {c.is_featured ? "Unpin" : "Feature"}
              </Button>
              <Button type="button" size="sm" variant="ghost"
                      onClick={() => setMsgTarget(c)}
                      className="text-teal-700 hover:bg-teal-50 shrink-0"
                      data-testid={`admin-coach-message-${c.id}`}>
                <MessageSquare className="w-3.5 h-3.5 mr-1" /> Message
              </Button>
              <Button type="button" size="sm" variant="ghost"
                      onClick={() => setRemoveTarget(c)}
                      className="text-rose-600 hover:bg-rose-50 shrink-0"
                      data-testid={`admin-coach-remove-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <AdminCoachMessageDialog target={msgTarget} onClose={() => setMsgTarget(null)} />
      <AdminCoachRemoveDialog
        target={removeTarget}
        onClose={() => setRemoveTarget(null)}
        onRemoved={(id) => {
          setCoaches((prev) => prev.filter((x) => x.id !== id));
          setRemoveTarget(null);
        }}
      />
      <AdminCoachBroadcastDialog
        open={broadcastOpen}
        totalCoaches={coaches.length}
        onClose={() => setBroadcastOpen(false)}
      />
    </div>
  );
}

function AdminCoachMessageDialog({ target, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!target) { setSubject(""); setBody(""); setHistory([]); return; }
    api.get(`/admin/coaches/${target.id}/messages`)
      .then(({ data }) => setHistory(Array.isArray(data) ? data : []))
      .catch(() => setHistory([]));
  }, [target]);

  if (!target) return null;

  const send = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/coaches/${target.id}/message`, { subject: subject.trim(), body: body.trim() });
      toast.success(data.delivered ? "Message sent" : "Saved · email delivery flaked (see history)");
      setSubject(""); setBody("");
      const { data: hist } = await api.get(`/admin/coaches/${target.id}/messages`).catch(() => ({ data: [] }));
      setHistory(hist || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="admin-coach-msg-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="admin-coach-msg-dialog">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">Message coach</div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">{target.name || target.email}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  data-testid="admin-coach-msg-close" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value.slice(0, 140))}
                   placeholder="e.g. Quick heads-up on the community rules"
                   className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                   data-testid="admin-coach-msg-subject" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Message</label>
            <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value.slice(0, 4000))}
                      placeholder="Write your message…"
                      className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
                      data-testid="admin-coach-msg-body" />
            <div className="text-[10px] text-slate-400 font-mono-num mt-1">{body.length}/4000</div>
          </div>
          {history.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">History</div>
              <ul className="space-y-1.5 max-h-40 overflow-auto" data-testid="admin-coach-msg-history">
                {history.map((h) => (
                  <li key={h.id} className="text-[12px] border border-slate-100 rounded-md p-2 bg-slate-50/40">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 truncate">{h.subject}</span>
                      <span className={`text-[10px] font-mono-num ${h.delivered ? "text-teal-700" : "text-rose-600"}`}>
                        {h.delivered ? "✓ delivered" : "! failed"}
                      </span>
                      <span className="text-[10px] text-slate-400 ml-auto">{(h.sent_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-slate-600 mt-0.5 line-clamp-2">{h.body}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}
                  className="text-slate-700 hover:bg-slate-100" data-testid="admin-coach-msg-cancel">Cancel</Button>
          <Button type="button" size="sm" onClick={send}
                  disabled={!subject.trim() || !body.trim() || sending}
                  className="text-white" style={{ background: TEAL }}
                  data-testid="admin-coach-msg-send">
            <Send className="w-3.5 h-3.5 mr-1" /> {sending ? "Sending…" : "Send"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AdminCoachRemoveDialog({ target, onClose, onRemoved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) setReason("");
  }, [target]);

  if (!target) return null;

  const doRemove = async () => {
    setBusy(true);
    try {
      const clean = reason.trim();
      const url = clean
        ? `/admin/coaches/${target.id}?reason=${encodeURIComponent(clean)}`
        : `/admin/coaches/${target.id}`;
      const { data } = await api.delete(url);
      toast.success(data?.reason_sent ? "Removed · reason emailed" : `Removed ${target.email}`);
      onRemoved(target.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="admin-coach-remove-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="admin-coach-remove-dialog">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-rose-600">Remove coach</div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">{target.name || target.email}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  aria-label="Close" data-testid="admin-coach-remove-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-600">
            Removes coach status and revokes every active client share. Optionally send a short reason so the coach hears why directly from you.
          </p>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Reason (optional · emailed to the coach)</label>
            <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value.slice(0, 600))}
                      placeholder="e.g. Community rules violation confirmed after review. Feel free to reapply after 90 days."
                      className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
                      data-testid="admin-coach-remove-reason" />
            <div className="text-[10px] text-slate-400 font-mono-num mt-1">{reason.length}/600</div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}
                  className="text-slate-700 hover:bg-slate-100" data-testid="admin-coach-remove-cancel">Cancel</Button>
          <Button type="button" size="sm" onClick={doRemove} disabled={busy}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="admin-coach-remove-confirm">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> {busy ? "Removing…" : "Remove coach"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AdminCoachBroadcastDialog({ open, totalCoaches, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) { setSubject(""); setBody(""); }
  }, [open]);

  if (!open) return null;

  const send = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/coaches/broadcast", { subject: subject.trim(), body: body.trim() });
      const failedPart = data.failed > 0 ? ` · ${data.failed} failed` : "";
      toast.success(`Broadcast sent to ${data.delivered} coach${data.delivered === 1 ? "" : "es"}${failedPart}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="admin-coach-broadcast-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="admin-coach-broadcast-dialog">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488] flex items-center gap-1.5">
              <Megaphone className="w-3.5 h-3.5" /> Broadcast to all coaches
            </div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5 flex items-center gap-2">
              Announcement
              <span className="inline-flex items-center gap-1 text-[11px] font-normal text-slate-500">
                <Users className="w-3 h-3" /> {totalCoaches} recipient{totalCoaches === 1 ? "" : "s"}
              </span>
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  aria-label="Close" data-testid="admin-coach-broadcast-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value.slice(0, 140))}
                   placeholder="e.g. New rating guidelines for February"
                   className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                   data-testid="admin-coach-broadcast-subject" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Message</label>
            <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value.slice(0, 4000))}
                      placeholder="This will be sent to every verified coach on the platform."
                      className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
                      data-testid="admin-coach-broadcast-body" />
            <div className="text-[10px] text-slate-400 font-mono-num mt-1">{body.length}/4000</div>
          </div>
          <div className="text-[11px] text-slate-500 italic border-l-2 border-teal-200 pl-3 py-1">
            Every send is logged in each coach's message history so you have an audit trail.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}
                  className="text-slate-700 hover:bg-slate-100" data-testid="admin-coach-broadcast-cancel">Cancel</Button>
          <Button type="button" size="sm" onClick={send}
                  disabled={!subject.trim() || !body.trim() || sending || totalCoaches === 0}
                  className="text-white" style={{ background: TEAL }}
                  data-testid="admin-coach-broadcast-send">
            <Send className="w-3.5 h-3.5 mr-1" /> {sending ? "Sending…" : `Send to ${totalCoaches}`}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
